<template>
  <div class="tasks-view">
    <YourTasksTab
      v-if="props.assigneeMe"
      :tasks="tasks"
      :pagination="pagination"
      :applied-filters="appliedFilters"
      :menu-items="menuItems"
      :loading="skeletonLoading"
      @paginationChange="onPageChange"
      @onFilterChange="onFilterChange"
      @removeFilter="onRemoveFilter"
      @search="onSearch"
      @chatter="openChatter"
      @upload="openUploadVideoWizard"
    />
    <TasksPage
      v-else
      :tasks="tasks"
      :menu-items="menuItems"
      :pagination="pagination"
      :applied-filters="appliedFilters"
      :loading="skeletonLoading"
      @paginationChange="onPageChange"
      @onFilterChange="onFilterChange"
      @removeFilter="onRemoveFilter"
      @search="onSearch"
      @chatter="openChatter"
      @upload="openUploadVideoWizard"
      @menuAction="onMenuAction"
    />
    <ChatterDrawer
      :drawer="chatterDrawer"
      type="Task"
      :typeValue="chatterTypeValue"
      :listData="chatterListData"
      :object-type="chatterListData?.chatterTable || 'common_uploadmicrolearningvideotask'"
      :object-id="chatterListData?.id ?? null"
      :chatterList="chatterList"
      :totalCheckpointChatter="totalTaskChatter"
      iconName="org_icon_checkpoint"
      @closeDrawer="closeChatterDrawer"
      @requestMoreCheckpointsChatter="requestMoreTaskChatter"
    />
    <UploadVideoWizard
      :value="showUploadWizard"
      :state="uploadWizardState"
      :file="uploadWizardFile"
      :progress="uploadWizardProgress"
      :result-message="resultMessage"
      :iq-generation-status="uploadWizardIqGenerationStatus"
      :iq-trace-entries="uploadWizardIqTraceEntries"
      :iq-generation-in-progress="uploadWizardIqGenerationInProgress"
      :show-iq-status-pills="false"
      :title="wizardTitle"
      submit-label="Upload"
      retry-label="Upload again"
      cancel-label="Discard"
      :equal-actions="true"
      done-label="Close"
      @input="onUploadWizardInput"
      @go-back="handleUploadWizardGoBack"
      @close="handleUploadWizardClose"
      @select="handleUploadWizardSelect"
      @remove="handleUploadWizardRemove"
      @submit="handleUploadWizardSubmit"
      @done="handleUploadWizardDone"
      @abort="handleUploadWizardAbort"
    />
    <CancelUploadTaskDialog
      :value="showCancelUploadTaskDialog"
      :loading="cancelUploadTaskLoading"
      @input="showCancelUploadTaskDialog = $event"
      @confirm="handleCancelUploadTaskConfirm"
    />
    <TaskEmployeeToUploadVideoDialog
      :dialog="showReassignUploadTaskDialog"
      :checkpoint-description="reassignCheckpointDescription"
      mode="reassign"
      submit-label="Reassign"
      :initial-employee="reassignInitialEmployee"
      :initial-deadline="reassignInitialDeadline"
      :wizard="false"
      @back="showReassignUploadTaskDialog = false"
      @onAction="handleReassignUploadTaskAction"
    />
  </div>
</template>

<script setup>
import { getCurrentInstance, ref, computed, onMounted, onUnmounted } from "vue";
import TasksPage from "@components/TasksPage/TasksPage.vue";
import YourTasksTab from "@components/YourTasksTab/YourTasksTab.vue";
import UploadVideoWizard from "@components/UploadVideoWizard/UploadVideoWizard.vue";
import ChatterDrawer from "@components/Drawer/ChatterDrawer.vue";
import CancelUploadTaskDialog from "@components/CancelUploadTaskDialog/CancelUploadTaskDialog.vue";
import TaskEmployeeToUploadVideoDialog from "@components/TaskEmployeeToUploadVideoDialog/TaskEmployeeToUploadVideoDialog.vue";
import { getAccountTasks } from "@/api/tasksAPI";
import {
  cancelUploadTask,
  reassignVideoUploadTask,
  sendUploadTaskReminder,
} from "@/api/responsibilityAPI";
import { useNotificationStore } from "@/store/pinia/notifications";
import {
  isMyTasksAssigneeFilter,
  TASKS_ASSIGNEE_FILTER_GROUP,
  MY_TASKS_FILTER_LABEL,
} from "@components/constants/tasksModule";
import { TASK_MENU_ACTION, TASK_MENU_ITEMS } from "@components/TasksTable/tasksTableCellUtils";
import { LD_FLAG_IDEA_MDP_560 } from "@components/constants/launchDarklyFlagKeys";
import { mapApiTasksToTableRows } from "./tasksTableMapper";
import { useTaskVideoUpload } from "./useTaskVideoUpload";
import { useTaskChatter } from "./useTaskChatter";

const PAGE_SIZE = 10;

const SEARCH_DEBOUNCE_MS = 300;

const STATUS_API_BY_FILTER = {
  "To Do": "PENDING",
  "In Progress": "PENDING",
  Completed: "COMPLETED",
  Canceled: "CANCELLED",
  Cancelled: "CANCELLED",
  Planned: "PENDING",
  to_do: "PENDING",
  in_progress: "PENDING",
  completed: "COMPLETED",
  canceled: "CANCELLED",
  cancelled: "CANCELLED",
  planned: "PENDING",
};

const props = defineProps({
  assigneeMe: {
    type: Boolean,
    default: false,
  },
});

const { proxy } = getCurrentInstance() || {};

const tasks = ref([]);
const pendingLoads = ref(0);
const skeletonLoadingEnabled = ref(false);
const skeletonLoading = computed(() => pendingLoads.value > 0 && skeletonLoadingEnabled.value);
const notificationStore = useNotificationStore();

const menuItems = computed(() => TASK_MENU_ITEMS);

const activeMenuTask = ref(null);
const showCancelUploadTaskDialog = ref(false);
const cancelUploadTaskLoading = ref(false);
const showReassignUploadTaskDialog = ref(false);
const sendReminderLoading = ref(false);

const appliedFilters = ref(["To Do"]);
const searchQuery = ref("");
const pagination = ref({
  page: 1,
  total: 0,
  perPage: PAGE_SIZE,
});
let searchTimer = null;

function getCurrentAccountId() {
  try {
    return JSON.parse(localStorage.getItem("currentAccountID"));
  } catch {
    return localStorage.getItem("currentAccountID");
  }
}

function isNonApiStatusFilterToken(filter) {
  const key = filterKey(filter);
  return key === "all" || key === "all_assignees";
}

function toApiStatus(filters) {
  const mapped = [
    ...new Set(
      (filters || [])
        .filter((filter) => !isMyTasksAssigneeFilter(filter) && !isNonApiStatusFilterToken(filter))
        .map((filter) => STATUS_API_BY_FILTER[filter])
        .filter(Boolean)
    ),
  ];
  return mapped.length ? mapped.join(",") : undefined;
}

function buildListParams(page) {
  const params = {
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  };
  const myTasksOnly =
    props.assigneeMe || (appliedFilters.value || []).some(isMyTasksAssigneeFilter);
  if (myTasksOnly) {
    params.assignee = "me";
  }
  const status = toApiStatus(appliedFilters.value);
  if (status) {
    params.status = status;
  }
  if (searchQuery.value) {
    params.search = searchQuery.value;
  }
  return params;
}

function showLoader() {
  if (skeletonLoadingEnabled.value) return;
  if (proxy?.$loader?.show) {
    proxy.$loader.show();
  }
}

function hideLoader() {
  if (skeletonLoadingEnabled.value) return;
  if (proxy?.$loader?.hide) {
    proxy.$loader.hide();
  }
}

async function loadTasks(page = pagination.value.page) {
  pendingLoads.value += 1;
  showLoader();
  try {
    const accountId = getCurrentAccountId();
    if (!accountId) {
      tasks.value = [];
      pagination.value = { ...pagination.value, page: 1, total: 0 };
      return;
    }
    const { data } = await getAccountTasks(accountId, buildListParams(page));
    const results = data && Array.isArray(data.results) ? data.results : [];
    tasks.value = mapApiTasksToTableRows(results);
    pagination.value = {
      page,
      total: typeof data?.count === "number" ? data.count : results.length,
      perPage: PAGE_SIZE,
    };
  } catch (error) {
    console.error("Failed to load account tasks:", error);
    tasks.value = [];
    pagination.value = { ...pagination.value, page, total: 0 };
  } finally {
    pendingLoads.value -= 1;
    hideLoader();
  }
}

function onPageChange(paginationData) {
  const nextPage = typeof paginationData === "number" ? paginationData : paginationData?.page || 1;
  loadTasks(nextPage);
}

function filterKey(filter) {
  return String(filter || "")
    .toLowerCase()
    .replace(/\s+/g, "_");
}

function normalizeFilterToken(item) {
  if (typeof item === "string") {
    return isNonApiStatusFilterToken(item) ? null : item;
  }
  if (item?.labelType === TASKS_ASSIGNEE_FILTER_GROUP) {
    if (item?.value === MY_TASKS_FILTER_LABEL) return "my_tasks";
    return null;
  }
  if (item?.value != null) {
    return isNonApiStatusFilterToken(item.value) ? null : item.value;
  }
  if (item?.label != null) {
    return isNonApiStatusFilterToken(item.label) ? null : item.label;
  }
  return null;
}

function onFilterChange(filters) {
  const nextFilters = (Array.isArray(filters) ? filters : [])
    .map(normalizeFilterToken)
    .filter(Boolean);
  appliedFilters.value = nextFilters;
  loadTasks(1);
}

function onRemoveFilter(removedFilter) {
  const removedKey = filterKey(removedFilter);
  appliedFilters.value = appliedFilters.value.filter((filter) => filterKey(filter) !== removedKey);
  loadTasks(1);
}

function onSearch(query) {
  searchQuery.value = String(query || "").trim();
  clearTimeout(searchTimer);
  if (!searchQuery.value) {
    loadTasks(1);
    return;
  }
  searchTimer = setTimeout(() => {
    loadTasks(1);
  }, SEARCH_DEBOUNCE_MS);
}

function formatUploadTaskDeadline(deadline) {
  if (typeof deadline === "string" && /^\d{4}-\d{2}-\d{2}$/.test(deadline)) {
    return deadline;
  }
  if (deadline instanceof Date && !Number.isNaN(deadline.getTime())) {
    return deadline.toISOString().slice(0, 10);
  }
  if (deadline?.toISOString) {
    return deadline.toISOString().slice(0, 10);
  }
  const d = new Date(deadline);
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  return null;
}

const reassignInitialEmployee = computed(() => {
  const task = activeMenuTask.value;
  if (!task) return null;
  const name = task.assignedTo?.name || "";
  return {
    id: task.assigneeId,
    name,
    fullName: name,
    firstName: name,
    lastName: "",
    access: true,
  };
});

const reassignInitialDeadline = computed(() => activeMenuTask.value?.deadline ?? null);

const reassignCheckpointDescription = computed(() => {
  const task = activeMenuTask.value;
  if (!task) return "";
  return task.criteria || task.summary || "";
});

function onMenuAction({ item, task }) {
  activeMenuTask.value = task;
  const action = item?.action;
  if (action === TASK_MENU_ACTION.CANCEL) {
    showCancelUploadTaskDialog.value = true;
  } else if (action === TASK_MENU_ACTION.REASSIGN) {
    showReassignUploadTaskDialog.value = true;
  } else if (action === TASK_MENU_ACTION.SEND_REMINDER) {
    handleSendReminder(task);
  }
}

async function handleCancelUploadTaskConfirm() {
  const task = activeMenuTask.value;
  const accountId = getCurrentAccountId();
  if (!accountId || !task || task.responsibilityId == null || task.checkpointId == null) return;

  cancelUploadTaskLoading.value = true;
  try {
    await cancelUploadTask(accountId, task.responsibilityId, task.checkpointId);
    showCancelUploadTaskDialog.value = false;
    notificationStore.success("Upload task cancelled");
    loadTasks(pagination.value.page);
  } catch (error) {
    console.error("Error canceling upload task:", error);
    const detail = error?.response?.data?.detail ?? error?.response?.data?.message;
    notificationStore.error(detail ?? "Failed to cancel upload task.");
  } finally {
    cancelUploadTaskLoading.value = false;
  }
}

async function handleReassignUploadTaskAction(assignPayload) {
  if (!assignPayload?.employee) return;

  const task = activeMenuTask.value;
  const accountId = getCurrentAccountId();
  if (!accountId || !task || task.responsibilityId == null || task.checkpointId == null) return;

  const notify =
    assignPayload.employee.access === true ? assignPayload.notifyEmployee === true : false;
  const payload = {
    assignee_id: assignPayload.employee.id,
    notify,
  };

  const deadlineStr = formatUploadTaskDeadline(assignPayload.deadline);
  const initialDeadlineStr = formatUploadTaskDeadline(reassignInitialDeadline.value);
  if (deadlineStr && deadlineStr !== initialDeadlineStr) {
    payload.deadline = deadlineStr;
  }

  try {
    await reassignVideoUploadTask(accountId, task.responsibilityId, task.checkpointId, payload);
    showReassignUploadTaskDialog.value = false;
    notificationStore.success("Task reassigned successfully");
    loadTasks(pagination.value.page);
  } catch (error) {
    console.error("Error reassigning upload task:", error);
    const detail = error?.response?.data?.detail ?? error?.response?.data?.message;
    notificationStore.error(detail ?? "Failed to reassign task.");
  }
}

async function handleSendReminder(task) {
  const accountId = getCurrentAccountId();
  if (!accountId || !task || task.responsibilityId == null || task.checkpointId == null) return;
  if (sendReminderLoading.value) return;

  sendReminderLoading.value = true;
  try {
    await sendUploadTaskReminder(accountId, task.responsibilityId, task.checkpointId);
    notificationStore.success("Reminder sent successfully");
  } catch (error) {
    console.error("Error sending upload task reminder:", error);
    const detail = error?.response?.data?.detail ?? error?.response?.data?.message;
    if (detail === "ASSIGNEE_EMAIL_MISSING") {
      notificationStore.error(
        "This employee has no access to the Employee Portal. Please grant him/her access first and try again."
      );
    } else {
      notificationStore.error(detail ?? "Failed to send reminder.");
    }
  } finally {
    sendReminderLoading.value = false;
  }
}

const {
  showUploadWizard,
  uploadWizardState,
  uploadWizardFile,
  uploadWizardProgress,
  resultMessage,
  uploadWizardIqGenerationStatus,
  uploadWizardIqTraceEntries,
  uploadWizardIqGenerationInProgress,
  openUploadVideoWizard,
  onUploadWizardInput,
  handleUploadWizardGoBack,
  handleUploadWizardClose,
  handleUploadWizardSelect,
  handleUploadWizardRemove,
  handleUploadWizardSubmit,
  handleUploadWizardDone,
  handleUploadWizardAbort,
} = useTaskVideoUpload({
  onSuccess: () => loadTasks(pagination.value.page),
});

const wizardTitle = computed(() =>
  ["processing", "validating", "not-related"].includes(uploadWizardState.value)
    ? "Processing Video"
    : "Upload Microlearning Video"
);

const {
  drawer: chatterDrawer,
  typeValue: chatterTypeValue,
  listData: chatterListData,
  chatterList,
  totalCheckpointChatter: totalTaskChatter,
  openChatter,
  closeDrawer: closeChatterDrawer,
  requestMore: requestMoreTaskChatter,
} = useTaskChatter();

onMounted(async () => {
  if (typeof proxy?.$getFlag === "function") {
    try {
      skeletonLoadingEnabled.value = (await proxy.$getFlag(LD_FLAG_IDEA_MDP_560, false)) === true;
    } catch {
      skeletonLoadingEnabled.value = false;
    }
  }
  loadTasks(1);
});

onUnmounted(() => {
  clearTimeout(searchTimer);
});
</script>

<style scoped>
.tasks-view {
  width: 100%;
}
</style>
